import { useEffect, useState } from "react";
import { API_BASE } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { hasRemediationAccess } from "../../config/searchSpaces";

const EMPTY_ANALYSIS_DATA = {
  AINSTEIN_STATUS: "unknown",
  LAST_UPDATE: "",
  CLOSED_CASES: 0,
  OPEN_CASES: 0,
  TOTAL_CASES: 0,
  TYPES: [],
  LAST_WEEK: null,
  LAST_MONTH: null,
};

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Backend did not return valid JSON.");
  }
}

function getBackendErrorMessage(data, fallback) {
  return (
    data?.message ||
    data?.detail ||
    data?.error ||
    data?.errors?.[0]?.message ||
    fallback
  );
}

function normalizePeriod(period) {
  if (!period || typeof period !== "object") return null;

  return {
    NEW_CASES: toNumber(
      period.NEW_CASES ?? period.new_cases ?? period.newCases,
      0
    ),
    CLOSED_CASES: toNumber(
      period.CLOSED_CASES ?? period.closed_cases ?? period.closedCases,
      0
    ),
  };
}

function normalizeTypes(types) {
  if (!Array.isArray(types)) return [];

  return types.map((type) => ({
    TYPE_NAME: String(
      type.TYPE_NAME ??
      type.type_name ??
      type.remediation_type ??
      type.name ??
      "UNKNOWN_TYPE"
    ),
    AMOUNT: toNumber(
      type.AMOUNT ??
      type.amount ??
      type.total ??
      type.open_cases ??
      type.open ??
      0,
      0
    ),
    OPEN: toNumber(type.OPEN ?? type.open ?? 0, 0),
    CLOSED: toNumber(type.CLOSED ?? type.closed ?? 0, 0),
    TOTAL: toNumber(type.TOTAL ?? type.total ?? 0, 0),
  }));
}

function normalizeAnalysisData(raw = {}) {
  const generalData =
    raw.general_data ?? raw.GENERAL_DATA ?? raw.generalData ?? raw;

  const remediationTypes =
    raw.remediation_types ??
    raw.REMEDIATION_TYPES ??
    raw.remediationTypes ??
    raw.TYPES ??
    [];

  const normalizedTypes = normalizeTypes(remediationTypes);

  const openCasesFromTypes = normalizedTypes.reduce(
    (sum, type) => sum + Number(type.OPEN || 0),
    0
  );

  const closedCases = toNumber(
    generalData.closed_cases ??
    generalData.CLOSED_CASES ??
    generalData.closedCases,
    0
  );

  const openCases = toNumber(
    generalData.open_cases ?? generalData.OPEN_CASES ?? generalData.openCases,
    openCasesFromTypes
  );

  const totalCases = toNumber(
    generalData.total_cases ?? generalData.TOTAL_CASES ?? generalData.totalCases,
    openCases + closedCases
  );

  return {
    AINSTEIN_STATUS:
      generalData.ainstein_status ??
      generalData.AINSTEIN_STATUS ??
      generalData.status ??
      "online",
    LAST_UPDATE:
      generalData.last_update ??
      generalData.LAST_UPDATE ??
      generalData.lastUpdate ??
      "",
    CLOSED_CASES: closedCases,
    OPEN_CASES: openCases,
    TOTAL_CASES: totalCases,
    TYPES: normalizedTypes,
    LAST_WEEK: normalizePeriod(generalData.last_week ?? generalData.LAST_WEEK),
    LAST_MONTH: normalizePeriod(
      generalData.last_month ?? generalData.LAST_MONTH
    ),
  };
}

function isValidAnalysisData(data) {
  return isPlainObject(data) && Array.isArray(data.TYPES);
}

export function useAnalysisPageData(searchSpace) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [analysisData, setAnalysisData] = useState(EMPTY_ANALYSIS_DATA);

  useEffect(() => {
    let isCancelled = false;

    if (!hasRemediationAccess(searchSpace) || searchSpace !== "in") {
      setLoading(false);
      setStatus("");
      setError("");
      setAnalysisData(EMPTY_ANALYSIS_DATA);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setStatus("");
      setError("");

      try {
        const res = await fetchWithAuth(
          `${API_BASE}/ainstein/in/analysis-page-data`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        let raw = null;

        try {
          raw = await readJsonResponse(res);
        } catch (jsonError) {
          if (res.ok) {
            throw jsonError;
          }
        }

        if (!res.ok) {
          throw new Error(
            getBackendErrorMessage(
              raw,
              `Missing authorization or failed to load analysis data (status ${res.status}).`
            )
          );
        }

        const normalized = normalizeAnalysisData(raw?.data || raw);

        if (!isValidAnalysisData(normalized)) {
          throw new Error("Invalid analysis dataset returned by backend.");
        }

        if (!isCancelled) {
          setAnalysisData(normalized);
          setError("");
        }
      } catch (err) {
        if (!isCancelled) {
          setAnalysisData({
            ...EMPTY_ANALYSIS_DATA,
            AINSTEIN_STATUS: "offline",
          });

          setError(
            err?.message ||
            "Missing authorization or failed to load analysis data."
          );
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [searchSpace]);

  return {
    loading,
    status,
    error,
    analysisData,
  };
}